import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import PortalHeader from "@/components/PortalHeader";
import ChapterReader from "@/components/ChapterReader";
import DocumentReader from "@/components/DocumentReader";
import AppendixReader from "@/components/AppendixReader";
import RevenueCalculator from "@/components/RevenueCalculator";
import KnowledgeCheck from "@/components/KnowledgeCheck";
import Acknowledgement from "@/components/Acknowledgement";
import api from "@/lib/api";
import { BookOpen, FileText, Check, Clock, Lock, Sprout, ArrowRight } from "lucide-react";

export default function PortalPage() {
  const { user, logout } = useAuth();
  const [chapters, setChapters] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [appendices, setAppendices] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [progress, setProgress] = useState(null);
  const [summary, setSummary] = useState(null);
  const [openChapter, setOpenChapter] = useState(null);
  const [openDoc, setOpenDoc] = useState(null);
  const [openAppendix, setOpenAppendix] = useState(null);

  const loadProgress = async () => {
    const { data } = await api.get("/me/progress");
    setProgress(data.progress);
    setSummary(data.summary);
  };

  useEffect(() => {
    (async () => {
      const [c, d, a, q] = await Promise.all([
        api.get("/prospectus/chapters"),
        api.get("/documents"),
        api.get("/appendices"),
        api.get("/knowledge-check"),
      ]);
      setChapters(c.data.chapters);
      setDocuments(d.data.documents);
      setAppendices(a.data.appendices);
      setQuestions(q.data.questions);
      await loadProgress();
    })();
  }, []);

  const onProgressUpdate = (data) => {
    setProgress(data.progress);
    setSummary(data.summary);
  };

  const keyDocs = useMemo(() => documents.filter((d) => d.category === "key"), [documents]);
  const finDocs = useMemo(() => documents.filter((d) => d.category === "financial"), [documents]);
  const legDocs = useMemo(() => documents.filter((d) => d.category === "legal"), [documents]);

  const allChaptersDone = summary && summary.chapters_completed === summary.chapters_total;
  const kcPassed = !!summary?.knowledge_check_passed;
  const ackDone = !!summary?.acknowledgement_completed;

  return (
    <div className="min-h-screen">
      <PortalHeader user={user} onLogout={logout} />

      {/* Active grower banner */}
      {user?.application_status === "active" && (
        <div className="bg-gradient-to-r from-[var(--bloom-brand)] to-[var(--bloom-accent)] text-white" data-testid="active-grower-banner">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <Sprout size={18} strokeWidth={1.6} />
              <span className="font-serif italic">You're an active Heritage Bloom Club grower.</span>
            </div>
            <Link to="/grower" className="bloom-btn bg-white text-[var(--bloom-ink)] hover:bg-[#F4ECF7] !text-sm" data-testid="goto-grower-portal">
              Open Grower Portal <ArrowRight size={14} className="ml-2 inline" />
            </Link>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-12 relative" data-testid="portal-hero">
        <div className="bloom-divider mb-6">✦ Founding Grower Information Memorandum ✦</div>
        <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] max-w-3xl">
          Growing <em>The Future</em> Of Local Flowers.
        </h1>
        <p className="font-serif italic text-xl text-[var(--bloom-ink-soft)] mt-6 max-w-2xl">
          A national network connecting independent flower farms with local communities through
          seasonal subscriptions, shared systems and sustainable growth.
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)]">
          <span>Version 1.0</span>
          <span>·</span>
          <span>Founding Grower Release</span>
          <span>·</span>
          <span>2026 / 2027 Season</span>
        </div>
        <p className="mt-8 max-w-2xl text-sm text-[var(--bloom-ink-soft)] leading-relaxed border-l-2 border-[var(--bloom-brand)] pl-4 italic">
          Confidential. Prepared for prospective Founding Growers considering participation in the
          Heritage Bloom Club. Provided for discussion and evaluation purposes only and should not
          be reproduced or distributed without written permission from Heritage Dahlias NZ Ltd.
        </p>

        {/* Top-level progress */}
        {summary && (
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl" data-testid="portal-progress-summary">
            <ProgressTile label="Chapters Read" value={`${summary.chapters_completed} / ${summary.chapters_total}`} testid="summary-chapters" />
            <ProgressTile label="Documents Read" value={`${summary.documents_read} / ${summary.documents_total}`} testid="summary-documents" />
            <ProgressTile label="Time Spent" value={formatMins(summary.time_seconds)} testid="summary-time" />
            <ProgressTile
              label="Status"
              value={ackDone ? "Stage 3 Unlocked" : kcPassed ? "Final Step" : allChaptersDone ? "Knowledge Check" : "Reading"}
              accent={ackDone}
              testid="summary-status"
            />
          </div>
        )}
      </section>

      {/* SECTION 1, PROSPECTUS CHAPTERS */}
      <Section id="prospectus" eyebrow="Section 01" title="Founding Grower Prospectus" intro="Each chapter opens into a quiet reader. Time and completion are recorded. Nothing is rushed.">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="prospectus-grid">
          {chapters.map((c, i) => {
            const cp = progress?.chapters?.[c.id];
            const done = cp?.completed;
            return (
              <button
                key={c.id}
                onClick={() => setOpenChapter(c)}
                className="bloom-card text-left p-6 group relative"
                style={{ animationDelay: `${i * 30}ms` }}
                data-testid={`chapter-card-${c.number}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="bloom-chapter-number">{String(c.number).padStart(2, "0")}</div>
                  {done && <Check size={18} className="text-[var(--bloom-brand)] mt-2" />}
                </div>
                <h3 className="font-serif text-xl leading-tight mb-2">{c.title}</h3>
                <p className="font-serif italic text-sm text-[var(--bloom-ink-soft)] mb-4">{c.subtitle}</p>
                <div className="flex items-center gap-3 text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)]">
                  <BookOpen size={11} /> {c.read_minutes} min
                  {cp?.time_seconds > 0 && <><Clock size={11} /> {formatTime(cp.time_seconds)}</>}
                </div>
              </button>
            );
          })}
        </div>
      </Section>

      {/* SECTION 2, KEY DOCUMENTS */}
      <Section id="key-docs" eyebrow="Section 02" title="Key Documents" intro="The handbooks and standards that bring the Bloom Club to life on your farm.">
        <DocumentGrid docs={keyDocs} progress={progress} onOpen={setOpenDoc} prefix="key" />
      </Section>

      {/* SECTION 3, FINANCIAL */}
      <Section id="financial" eyebrow="Section 03" title="Financial Information" intro="The numbers behind the promise. Read the documents, then model your own revenue.">
        <DocumentGrid docs={finDocs} progress={progress} onOpen={setOpenDoc} prefix="fin" />
        <div className="mt-12">
          <RevenueCalculator />
        </div>
      </Section>

      {/* SECTION 4, LEGAL (read-only) */}
      <Section id="legal" eyebrow="Section 04" title="Legal Documents" intro="Read-only in Stage 2. Actual signing occurs in Stage 3.">
        <DocumentGrid docs={legDocs} progress={progress} onOpen={(d) => setOpenDoc({ ...d, readonly: true })} prefix="leg" />
      </Section>

      {/* SECTION 4b, APPENDICES (reference) */}
      <Section id="appendices" eyebrow="Section 04 · Reference" title="Appendices" intro="Quick reference material. Financial scenarios, FAQs, readiness checklist and plain English agreement summary.">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="appendix-grid">
          {appendices.map((a, i) => (
            <button
              key={a.id}
              onClick={() => setOpenAppendix(a)}
              className="bloom-card text-left p-6 flex flex-col group"
              style={{ animationDelay: `${i * 30}ms` }}
              data-testid={`appendix-card-${a.letter}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="bloom-chapter-number" style={{ fontSize: "2.5rem" }}>{a.letter}</div>
                <FileText className="text-[var(--bloom-ink-soft)]" strokeWidth={1.5} size={18} />
              </div>
              <h4 className="font-serif text-lg leading-tight mb-2">{a.title}</h4>
              <p className="text-sm text-[var(--bloom-ink-soft)] line-clamp-2 mb-4">{a.subtitle}</p>
              <div className="mt-auto text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-brand)]">
                Open Appendix →
              </div>
            </button>
          ))}
        </div>
      </Section>

      {/* SECTION 5, KNOWLEDGE CHECK */}
      <Section id="knowledge" eyebrow="Section 05" title="Knowledge Check" intro="Five short questions. 100% required to proceed.">
        <KnowledgeCheck
          questions={questions}
          locked={!allChaptersDone}
          lastResult={progress?.knowledge_check}
          onSubmitted={loadProgress}
        />
      </Section>

      {/* SECTION 6, ACKNOWLEDGEMENT */}
      <Section id="acknowledge" eyebrow="Section 06" title="Final Acknowledgement" intro="One signature. Stage 3 unlocks immediately.">
        <Acknowledgement
          locked={!kcPassed}
          completed={ackDone}
          onComplete={(d) => {
            setProgress(d.progress);
            setSummary(d.summary);
          }}
        />
      </Section>

      {/* Footer */}
      <footer className="border-t border-[var(--bloom-border)] mt-20 py-12 text-center">
        <div className="bloom-seal mx-auto mb-4">HBC<br/>2026</div>
        <div className="font-serif italic text-sm text-[var(--bloom-ink-soft)]">
          Heritage Dahlias NZ Ltd · Aotearoa New Zealand
        </div>
        <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)] mt-2">
          Founding Grower Information Memorandum · Confidential
        </div>
      </footer>

      {openChapter && (
        <ChapterReader
          chapter={openChapter}
          progress={progress}
          onClose={() => setOpenChapter(null)}
          onProgressUpdate={onProgressUpdate}
        />
      )}
      {openDoc && (
        <DocumentReader
          document={openDoc}
          progress={progress}
          readonly={openDoc.readonly}
          onClose={() => setOpenDoc(null)}
          onProgressUpdate={onProgressUpdate}
        />
      )}
      {openAppendix && (
        <AppendixReader
          appendix={openAppendix}
          onClose={() => setOpenAppendix(null)}
        />
      )}
    </div>
  );
}

function Section({ id, eyebrow, title, intro, children }) {
  return (
    <section id={id} className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 border-t border-[var(--bloom-border)]" data-testid={`section-${id}`}>
      <div className="mb-12 max-w-3xl">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--bloom-ink-muted)] mb-4">✦ {eyebrow} ✦</div>
        <h2 className="font-serif text-4xl md:text-5xl leading-tight mb-4">{title}</h2>
        <p className="font-serif italic text-lg text-[var(--bloom-ink-soft)]">{intro}</p>
      </div>
      {children}
    </section>
  );
}

function ProgressTile({ label, value, accent, testid }) {
  return (
    <div className="border-l border-[var(--bloom-border)] pl-4" data-testid={testid}>
      <div className="bloom-label mb-2">{label}</div>
      <div className={`font-serif text-2xl md:text-3xl leading-tight ${accent ? "text-[var(--bloom-brand)]" : ""}`}>{value}</div>
    </div>
  );
}

function DocumentGrid({ docs, progress, onOpen, prefix }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid={`docs-grid-${prefix}`}>
      {docs.map((d, i) => {
        const isRead = !!progress?.documents?.[d.id]?.read;
        return (
          <button
            key={d.id}
            onClick={() => onOpen(d)}
            className="bloom-card text-left p-6 flex flex-col group"
            style={{ animationDelay: `${i * 30}ms` }}
            data-testid={`doc-card-${d.id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <FileText className="text-[var(--bloom-ink-soft)]" strokeWidth={1.5} />
              {isRead && <Check size={16} className="text-[var(--bloom-brand)]" />}
            </div>
            <h4 className="font-serif text-lg leading-tight mb-2">{d.title}</h4>
            <p className="text-sm text-[var(--bloom-ink-soft)] line-clamp-2 mb-4">{d.description}</p>
            <div className="mt-auto flex items-center justify-between text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)]">
              <span>{d.pages} pages</span>
              <span className="text-[var(--bloom-brand)]">{isRead ? "Read ✓" : "View →"}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function formatTime(s) {
  if (!s) return "0s";
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m === 0) return `${r}s`;
  return `${m}m`;
}

function formatMins(s) {
  if (!s) return "0 min";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}
